import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { translate } from 'react-i18next';
import * as d3 from 'd3';
import moment from 'moment';
import ChartTooltip from './ChartTooltip';

const svgContainerHeight = 40;
const svgAxisInitialWidth = 100;
const svgAxisHeight = 20;

class TimelineChart extends Component {
  static propTypes = {
    startTime: PropTypes.number.isRequired,
    endTime: PropTypes.number.isRequired,
    machineId: PropTypes.string.isRequired,
    period: PropTypes.oneOf([
      'hour', 'day', 'week', 'month',
    ]).isRequired,
    values: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      timestamp: PropTypes.number.isRequired,
      red: PropTypes.number.isRequired,
      yellow: PropTypes.number.isRequired,
      green: PropTypes.number.isRequired,
    })).isRequired,
    t: PropTypes.func.isRequired,
  }

  constructor(props) {
    super(props);
    this.state = {
      hoverLeft: '',
      hoverParams: {},
    };

    this.axis = null;
    this.startTime = 0;
    this.endTime = 0;

    this.resized = this.resized.bind(this);
    window.addEventListener('resize', this.resized);
  }

  componentDidMount() {
    const {
      machineId, values, startTime, endTime, t,
    } = this.props;

    const svgContainer = d3.select(`#flagschart-${machineId}`)
      .append('svg')
      .attr('width', '100%')
      .attr('height', svgContainerHeight);

    d3.select(`#flagschart-${machineId}`)
      .append('div');

    svgContainer.append('rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', '100%')
      .attr('height', svgContainerHeight)
      .attr('fill', 'white')
      .attr('stroke', 'grey')
      .attr('stroke-width', 1);

    if (values.length === 0) {
      svgContainer.append('rect')
        .attr('x', 0)
        .attr('y', 0)
        .attr('width', '100%')
        .attr('height', svgContainerHeight)
        .attr('fill', '#dedaf7')
        .attr('class', 'flagchart-container')
        .on('mouseover', () => {
          this.setState({
            hoverLeft: `${50 - 7.5}%`,
            hoverParams: {
              'charts.light': t('common.off'),
            },
          });
        })
        .on('mouseout', () => {
          this.setState({ hoverLeft: '' });
        });
    } else {
      const totalDuration = endTime - startTime;

      if (startTime < values[0].timestamp) {
        values.unshift({
          id: 'filler',
          timestamp: startTime,
          green: 0,
          yellow: 0,
          red: 0,
        });
      }

      svgContainer.selectAll('g')
        .data(values)
        .enter()
        .append('rect')
        .attr('height', svgContainerHeight)
        .attr('x', (d) => {
          d.x = (d.timestamp - startTime) / totalDuration * 100; // eslint-disable-line no-param-reassign
          return `${d.x}%`;
        })
        .attr('y', 0)
        .attr('width', (d, i) => {
          const nextTimestamp = (i !== values.length - 1) ? values[i + 1].timestamp : endTime;
          d.width = (nextTimestamp - d.timestamp) / totalDuration * 100; // eslint-disable-line no-param-reassign
          return `${d.width}%`;
        })
        .attr('fill', (d) => {
          if (d.green) {
            return '#508642';
          }
          if (d.yellow) {
            return '#e0752d';
          }
          if (d.red) {
            return '#e24d42';
          }
          return '#dedaf7';
        })
        .attr('class', 'flagchart-container')
        .on('mouseover', (d, i) => {
          const nextTimestamp = (i !== values.length - 1) ? values[i + 1].timestamp : endTime;
          const duration = nextTimestamp - d.timestamp;

          let light;
          if (d.green) {
            light = 'common.green';
          } else if (d.yellow) {
            light = 'common.yellow';
          } else if (d.red) {
            light = 'common.red';
          } else {
            light = 'common.off';
          }

          this.setState({
            hoverLeft: `${d.x + (d.width / 2) - 7.5}%`,
            hoverParams: {
              'charts.started': moment(d.timestamp).format('DD/MM/YY HH:mm'),
              'charts.finished': moment(nextTimestamp).format('DD/MM/YY HH:mm'),
              'common.duration': `${Math.ceil(duration / 1000 / 60)} mins`,
              'charts.light': t(light),
            },
          });
        })
        .on('mouseout', () => {
          this.setState({ hoverLeft: '' });
        });
    }

    const axis = d3.select(`#flagschart-${machineId}-axis`)
      .append('svg')
      .attr('viewBox', `0 0 ${svgAxisInitialWidth} ${svgAxisHeight}`);

    axis.append('g')
      .attr('height', svgAxisHeight);

    this.axis = axis;
    this.startTime = startTime;
    this.endTime = endTime;

    // setup initial size correctly
    this.resized();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.resized);
  }

  resized() {
    if (this.axis === null) {
      return;
    }

    const { machineId, period } = this.props;

    let timeFormat;
    switch (period) {
      case 'hour':
      case 'day':
        timeFormat = '%H:%M';
        break;
      case 'week':
        timeFormat = '%d %b';
        break;
      case 'month':
        timeFormat = '%d %b';
        break;
      default:
        break;
    }

    const svgWidth = parseFloat(d3.select(`#flagschart-${machineId}`).style('width').replace(/px/, ''), 10);

    const scale = d3.scaleTime()
      .domain([this.startTime, this.endTime])
      .range([0, svgWidth - 1]);
    const xAxis = d3.axisBottom(scale)
      .ticks(10)
      .tickFormat(d3.timeFormat(timeFormat));

    this.axis
      .attr('viewBox', `0 0 ${svgWidth} ${svgAxisHeight}`)
      // .attr('font-size', svgAxisHeight * 2 / 3)
      .call(xAxis);
  }

  render() {
    const { machineId } = this.props;
    const { hoverLeft, hoverParams } = this.state;
    return (
      <div>
        <div id={`flagschart-${machineId}`} style={{ lineHeight: '0px' }} />
        <div id={`flagschart-${machineId}-axis`} style={{ lineHeight: '0px' }} />
        {hoverLeft.length > 0 && <ChartTooltip marginLeft={hoverLeft} params={hoverParams} />}
      </div>
    );
  }
}

export default translate()(TimelineChart);



// WEBPACK FOOTER //
// ./src/components/reusable/TimelineChart.js